import type { LeadPosition, PositionRowId } from '../../../types';

export function isPositionRowId(v: unknown): v is PositionRowId {
  if (typeof v === 'number' && Number.isFinite(v)) return true;
  if (typeof v === 'string' && v.trim().length > 0) return true;
  return false;
}

export function positionRowIdKey(id: PositionRowId): string {
  return String(id);
}

export function positionRowIdsEqual(a: PositionRowId, b: PositionRowId): boolean {
  return String(a) === String(b);
}

export function collectPositionRowIds(
  values: Array<PositionRowId | null | undefined>,
): PositionRowId[] {
  const seen = new Set<string>();
  const out: PositionRowId[] = [];
  for (const x of values) {
    if (!isPositionRowId(x)) continue;
    const key = positionRowIdKey(x);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(x);
  }
  return out;
}

export function activePositionEditKeysFromCounts(counts: ReadonlyMap<string, number>): Set<string> {
  return new Set(counts.keys());
}

export function incrementPositionFieldEditCount(counts: Map<string, number>, id: PositionRowId): void {
  const key = positionRowIdKey(id);
  counts.set(key, (counts.get(key) ?? 0) + 1);
}

/** Decrements after focus may move to another field in the same row (defer via microtask). */
export function decrementPositionFieldEditCount(counts: Map<string, number>, id: PositionRowId): void {
  const key = positionRowIdKey(id);
  const next = (counts.get(key) ?? 0) - 1;
  if (next <= 0) counts.delete(key);
  else counts.set(key, next);
}

/** Preserve unsaved in-flight field values when a background positions reload runs. */
export function mergePositionsKeepingInFlightEdits(
  prev: LeadPosition[],
  incoming: LeadPosition[],
  editingIds: ReadonlySet<string>,
): LeadPosition[] {
  if (editingIds.size === 0) return incoming;
  const prevByKey = new Map(prev.map((p) => [positionRowIdKey(p.id), p]));
  return incoming.map((serverRow) => {
    const key = positionRowIdKey(serverRow.id);
    const local = prevByKey.get(key);
    if (!local || !editingIds.has(key)) return serverRow;
    return {
      ...serverRow,
      item_name: local.item_name,
      description: local.description,
      internal_note: local.internal_note ?? '',
      quantity: local.quantity,
      unit: local.unit,
      unit_price_vk: local.unit_price_vk,
      unit_cost_ek: local.unit_cost_ek,
      surcharge_percent: local.surcharge_percent,
      line_total: local.line_total,
    };
  });
}
